import type { Ref } from 'vue'
import type { EventDateType } from '~/composables/types/event'
import {
  useDateRequestFormStorage,
  type DateRequestFormData,
} from '~/composables/useDateRequestFormStorage'

export interface DateRequestFormModel {
  email: string
  phone: string
  name: string
  surname: string
  numberOfTickets: number | null
  companyName: string
  city: string
  dateType: EventDateType | null
  dates: string[]
  additionalInfo?: string
}

const createEmptyFormModel = (): DateRequestFormModel => ({
  email: '',
  phone: '',
  name: '',
  surname: '',
  numberOfTickets: null,
  companyName: '',
  city: '',
  dateType: null,
  dates: [],
  additionalInfo: '',
})

const normalizeDateType = (
  value: DateRequestFormData['dateType']
): EventDateType | null =>
  value === 'PRIVATE' || value === 'PUBLIC' ? value : null

/**
 * Owns the date-request form ref and a single sessionStorage hydration path.
 * Ticket count is never restored from persisted data (see applySavedData). Buy-box
 * party size is applied only after the user changes quantity in the buy box (see
 * applySuggestedTicketCount).
 */
export const useDateRequestFormState = (): {
  form: Ref<DateRequestFormModel>
} => {
  const form = ref<DateRequestFormModel>(createEmptyFormModel())

  const { savedFormData, loaded } = useDateRequestFormStorage()
  const { dateRequestSuggestedTicketCount, isDateRequestSidebarOpen } =
    useUiState()
  const eventData = useEventData()

  const applySavedData = (data: DateRequestFormData) => {
    form.value = {
      ...createEmptyFormModel(),
      ...data,
      numberOfTickets: null,
      dateType: normalizeDateType(data.dateType),
      dates: [...(data.dates ?? [])],
    }
  }

  const applySuggestedTicketCount = () => {
    const suggested = dateRequestSuggestedTicketCount.value
    if (suggested == null || suggested < 1) {
      return
    }
    form.value.numberOfTickets = suggested
  }

  // Hydrate contact fields from session storage (ticket count is always reset).
  watch(
    [loaded, savedFormData, eventData],
    ([isLoaded, data]) => {
      if (!isLoaded || !data) {
        return
      }
      applySavedData(data)
      applySuggestedTicketCount()
    },
    { immediate: true }
  )

  // Re-apply buy-box party size when the user has changed quantity and the sidebar opens.
  watch(
    [dateRequestSuggestedTicketCount, isDateRequestSidebarOpen],
    () => {
      if (!isDateRequestSidebarOpen.value) {
        return
      }
      applySuggestedTicketCount()
    },
    { immediate: true }
  )

  return {
    form,
  }
}
